import { useCallback, useReducer } from 'react'
import effects from 'app/effects/effects'
import {
  union,
  compose,
  prop,
  sortBy,
  find,
  isNil,
  defaultTo,
  when,
  mergeRight,
  filter,
  map,
  trim,
  any,
  append,
  propEq,
  addIndex,
} from 'ramda'

const actionType = {
  START_FETCH_LANGUAGE: 'START_FETCH_LANGUAGE',
  END_FETCH_LANGUAGE: 'END_FETCH_LANGUAGE',
  UPDATE_ORIGINAL_TEXT: 'UPDATE_ORIGINAL_TEXT',
  UPDATE_TRANSLATION_TEXT: 'UPDATE_TRANSLATION_TEXT',
  START_TRANSLATION: 'START_TRANSLATION',
  SET_DETECT_LANGUAGE: 'SET_DETECT_LANGUAGE',
  UPDATE_DATE_LANGUAGE_LIST: 'UPDATE_DATE_LANGUAGE_LIST',
  UPDATE_LANGUAGE_SELECTED: 'UPDATE_LANGUAGE_SELECTED',
  UPDATE_TEXT_ADN_SET_DETECT_LANGUAGE: 'UPDATE_TEXT_ADN_SET_DETECT_LANGUAGE',
  ERROR_TRANSLATION: 'ERROR_TRANSLATION',
  UPDATE_TRANSLATED_ID: 'UPDATE_TRANSLATED_ID',
  CLOSE_LANGUAGE_SELECTOR: 'CLOSE_LANGUAGE_SELECTOR',
  OPEN_LANGUAGE_SELECTOR: 'OPEN_LANGUAGE_SELECTOR',
  START_DETECT_LANGUAGE: 'START_DETECT_LANGUAGE',
  OPEN_AND_FETCH_LANGUAGE_SELECTOR: 'OPEN_AND_FETCH_LANGUAGE_SELECTOR',
  CLEAR_LANGUAGE_SELECTED: 'CLEAR_LANGUAGE_SELECTED',
}

// Helper functions for text content
const defaultTextContent = {
  title: '',
  body: '',
  question: '',
  options: [],
}

const ensureTextContent = (payload) => ({
  title: defaultTo('', payload.title),
  body: defaultTo('', payload.body),
  question: defaultTo('', payload.question),
  options: defaultTo([], payload.options),
})

// Helper to extract option titles for API
const extractOptionTitles = (options) => {
  if (!options || !Array.isArray(options)) return []
  return compose(
    map(compose(trim, prop('title'))),
    filter((opt) => {
      const title = prop('title', opt)
      return title && trim(title) !== ''
    }),
    filter(Boolean),
  )(options)
}

// Helper to merge translated options with original imagePaths
const mergeTranslatedOptions = (translatedTitles, originalOptions) => {
  if (
    !Array.isArray(translatedTitles) ||
    !Array.isArray(originalOptions) ||
    originalOptions.length === 0
  ) {
    return undefined
  }

  const originalOptionsWithTitles = filter(
    compose((title) => title && title.trim() !== '', prop('title')),
    originalOptions,
  )

  // Use addIndex to get index parameter in map
  const mapWithIndex = addIndex(map)
  return mapWithIndex((translatedTitle, index) => {
    const originalOption = originalOptionsWithTitles[index]
    return {
      title:
        translatedTitle ||
        (originalOption ? prop('title', originalOption) : '') ||
        '',
      imagePath: originalOption
        ? prop('imagePath', originalOption) || null
        : null,
    }
  }, translatedTitles)
}

// Helper to build translate data object conditionally
const buildTranslateData = ({
  languageId,
  topicIds,
  title,
  body,
  question,
  options,
}) => {
  const baseData = {
    languageId,
    topicIds: topicIds.split(','),
  }

  const optionTitles = extractOptionTitles(options)

  return compose(
    when(() => title !== undefined && title !== '', mergeRight({ title })),
    when(() => body !== undefined && body !== '', mergeRight({ body })),
    when(
      () => question !== undefined && question !== '',
      mergeRight({ question }),
    ),
    when(() => optionTitles.length > 0, mergeRight({ options: optionTitles })),
  )(baseData)
}

// Helper to check if text has meaningful content (not empty HTML)
const hasContent = (text) => {
  if (!text) return false
  const trimmed = trim(text)
  return (
    trimmed !== '' &&
    trimmed !== '<p></p>' &&
    trimmed !== '<p style="word-break: break-word"></p>'
  )
}

// Helper to build detect language payload
const buildDetectLanguagePayload = (
  original,
  currentQuestion,
  currentOptions,
) => {
  const question =
    currentQuestion !== undefined ? currentQuestion : original.question
  const options =
    currentOptions !== undefined ? currentOptions : original.options
  const hasQuestion = hasContent(question)
  const hasOptions =
    Array.isArray(options) &&
    any((opt) => opt && hasContent(prop('title', opt)), options)

  if (hasQuestion || hasOptions) {
    // Poll data
    return compose(
      when(
        () => hasOptions,
        mergeRight({
          options: compose(
            map(compose(trim, prop('title'))),
            filter((opt) => opt && hasContent(prop('title', opt))),
          )(options),
        }),
      ),
      when(() => hasQuestion, mergeRight({ question })),
    )({})
  }

  // General/Event data
  return compose(
    when(() => hasContent(original.body), mergeRight({ body: original.body })),
    when(
      () => hasContent(original.title),
      mergeRight({ title: original.title }),
    ),
  )({})
}

const initialState = {
  languages: [],
  isLoading: false,
  isTranslating: false,
  original: defaultTextContent,
  textDisplay: defaultTextContent,
  languageId: undefined,
  error: undefined,
  translates: [],
  topics: [],
  topicsDisplay: [],
  originalTopics: [],
  isDetectLanguageLoading: false,
  originalLanguageId: undefined,
  openLanguageSelector: false,
}

function reduce(state, action) {
  switch (action.type) {
    case actionType.START_FETCH_LANGUAGE:
      return {
        ...state,
        isLoading: true,
        openLanguageSelector: true,
      }
    case actionType.END_FETCH_LANGUAGE:
      return {
        ...state,
        isLoading: false,
        languages: action.payload,
        originalLanguageId: action.originalLanguageId,
      }
    case actionType.UPDATE_ORIGINAL_TEXT: {
      const textContent = ensureTextContent(action.payload)
      return {
        ...state,
        languageId: undefined,
        originalLanguageId: undefined,
        original: textContent,
        textDisplay: textContent,
        originalTopics: action.payload.topics,
        topicsDisplay: action.payload.topics,
        topics: [],
        translates: [],
        error: undefined,
      }
    }
    case actionType.START_TRANSLATION:
      return {
        ...state,
        isTranslating: true,
      }
    case actionType.UPDATE_TRANSLATION_TEXT: {
      const mergeTextDisplay = (field) =>
        action.payload[field] !== undefined
          ? action.payload[field]
          : state.textDisplay[field]

      return {
        ...state,
        isTranslating: false,
        textDisplay: {
          title: mergeTextDisplay('title'),
          body: mergeTextDisplay('body'),
          question: mergeTextDisplay('question'),
          options: mergeTextDisplay('options'),
        },
        topicsDisplay: action.payload.topicsDisplay,
        topics: action.payload.topics,
        translates: union(state.translates, [action.payload]),
        error: undefined,
      }
    }
    case actionType.UPDATE_TEXT_ADN_SET_DETECT_LANGUAGE: {
      const textContent = ensureTextContent(action.payload)
      return {
        ...state,
        original: textContent,
        textDisplay: textContent,
        languageId: action.payload.languageId,
        translates: [
          {
            ...textContent,
            id: action.payload.languageId,
          },
        ],
        error: undefined,
      }
    }
    case actionType.SET_DETECT_LANGUAGE:
      return {
        ...state,
        languages: action.payload,
      }
    case actionType.UPDATE_DATE_LANGUAGE_LIST: {
      const textContent = ensureTextContent(action.payload)
      return {
        ...state,
        languages: action.payload.languages,
        original: textContent,
        textDisplay: textContent,
        languageId: action.payload.languageId,
        translates: [
          {
            ...textContent,
            id: action.payload.languageId,
          },
        ],
      }
    }
    case actionType.UPDATE_LANGUAGE_SELECTED:
      return {
        ...state,
        languageId: action.payload,
      }
    case actionType.ERROR_TRANSLATION:
      return {
        ...state,
        error: action.payload,
        isTranslating: false,
      }
    case actionType.OPEN_AND_FETCH_LANGUAGE_SELECTOR:
      return {
        ...state,
        openLanguageSelector: true,
        isDetectLanguageLoading: true,
      }
    case actionType.OPEN_LANGUAGE_SELECTOR:
      return {
        ...state,
        openLanguageSelector: true,
      }
    case actionType.CLOSE_LANGUAGE_SELECTOR:
      return {
        ...state,
        openLanguageSelector: false,
      }
    case actionType.DETECT_LANGUAGE_SUCCESS:
      return {
        ...state,
        isDetectLanguageLoading: false,
        languages: action.languages,
        originalLanguageId: action.originalLanguageId,
      }
    case actionType.CLEAR_LANGUAGE_SELECTED:
      return {
        ...state,
        languages: [],
        isLoading: false,
        isTranslating: false,
        languageId: undefined,
        error: undefined,
        translates: [],
        isDetectLanguageLoading: false,
        originalLanguageId: undefined,
        openLanguageSelector: false,
        topics: [],
      }
    default:
      return state
  }
}

export const useTranslationPreviewLanguage = () => {
  const [state, dispatch] = useReducer(reduce, initialState)

  const apiFetchersLanguagesCallback = useCallback(
    ({ subGroupIds, subscriberIds, topicIds, channelIds }) => {
      dispatch({ type: actionType.START_FETCH_LANGUAGE })
      effects.apis
        .getLanguageBySubGroups({
          subgroups: subGroupIds.join(','),
          subscribers: subscriberIds.join(','),
          topicIds: topicIds,
          channels: channelIds,
          isAllPlaceLanguage: false,
        })
        .then((resp) => {
          dispatch({
            type: actionType.END_FETCH_LANGUAGE,
            payload: compose(sortBy(prop('name')))(resp.data),
            originalLanguageId: state.originalLanguageId,
          })
        })
    },
    [],
  )

  const apiFetchersLanguagesCallbackCompose = useCallback(
    ({
      subGroupIds,
      subscriberIds,
      topicIds,
      channelIds,
      composeTitle,
      composeBody,
      composeDescription,
      voucherTitle,
      voucherDescription,
      composeQuestion,
      composeOptions,
    }) => {
      dispatch({ type: actionType.START_FETCH_LANGUAGE })
      effects.apis
        .getLanguageBySubGroupsCompose({
          subgroups: subGroupIds,
          subscribers: subscriberIds,
          topicIds,
          channels: channelIds,
          composeTitle,
          composeBody,
          composeDescription,
          voucherTitle,
          voucherDescription,
          composeQuestion,
          composeOptions,
          isAllPlaceLanguage: false,
        })
        .then((resp) => {
          const languages = resp.languages.data
          const detectLanguage = mergeRight(resp.detectLanguages, { count: 0 })

          const checkDetectLanguages = find(
            propEq('id', detectLanguage.id),
            languages,
          )

          const mergedLanguage = isNil(checkDetectLanguages)
            ? append(detectLanguage, languages)
            : languages

          dispatch({
            type: actionType.END_FETCH_LANGUAGE,
            payload: compose(sortBy(prop('name')))(mergedLanguage),
            originalLanguageId: detectLanguage.id,
          })
        })
    },
    [],
  )

  const updateOriginalText = ({ title, body, topics, question, options }) => {
    dispatch({
      type: actionType.UPDATE_ORIGINAL_TEXT,
      payload: { title, body, topics, question, options },
    })
  }

  const updateLanguageSelected = (event) => {
    dispatch({
      type: actionType.UPDATE_LANGUAGE_SELECTED,
      payload: event.target.value,
    })
  }

  const translateText = ({
    languageId,
    title,
    body,
    topicIds,
    topics,
    question,
    options,
  }) => {
    dispatch({ type: actionType.START_TRANSLATION })

    const translated = find(propEq('id', languageId), state.translates)
    const isHaveTranslation = !isNil(translated)
    const isOriginalLanguage = languageId === state.originalLanguageId

    if (isHaveTranslation || isOriginalLanguage) {
      const payload =
        isHaveTranslation && !isOriginalLanguage
          ? {
              title: translated.title,
              body: translated.body,
              question: translated.question,
              options: translated.options,
              id: translated.id,
              topics: state.topics,
              topicsDisplay: state.topics[languageId],
            }
          : {
              ...state.original,
              id: languageId,
              topics: state.topics,
              topicsDisplay: state.originalTopics,
            }

      dispatch({
        type: actionType.UPDATE_TRANSLATION_TEXT,
        payload,
      })
    } else {
      const translateData = buildTranslateData({
        languageId,
        topicIds,
        title,
        body,
        question,
        options,
      })

      const originalOptionsForMerge = options

      effects.apis
        .translate({ data: translateData })
        .then((result) => {
          const newTopics = mergeRight(state.topics, {
            [languageId]: result.topics,
          })

          const translatedOptions = mergeTranslatedOptions(
            result.options,
            originalOptionsForMerge,
          )

          dispatch({
            type: actionType.UPDATE_TRANSLATION_TEXT,
            payload: {
              title: result.title,
              body: result.body,
              question: result.question,
              options: translatedOptions,
              id: languageId,
              topics: newTopics,
              topicsDisplay: result.topics,
            },
          })
        })
        .catch((error) => {
          dispatch({ type: actionType.ERROR_TRANSLATION, payload: error })
        })
    }
  }

  const onOpenSelected = (currentQuestion, currentOptions) => {
    dispatch({
      type: actionType.OPEN_LANGUAGE_SELECTOR,
    })
    // If originalLanguageId is not set, trigger language detection
    if (isNil(state.originalLanguageId)) {
      onOpenLanguageSelected(currentQuestion, currentOptions)
    }
  }

  const onOpenLanguageSelected = (currentQuestion, currentOptions) => {
    const notHasOriginalLanguageId = isNil(state.originalLanguageId)
    dispatch({
      type: notHasOriginalLanguageId
        ? actionType.OPEN_AND_FETCH_LANGUAGE_SELECTOR
        : actionType.OPEN_LANGUAGE_SELECTOR,
    })

    if (notHasOriginalLanguageId) {
      const detectData = buildDetectLanguagePayload(
        state.original,
        currentQuestion,
        currentOptions,
      )

      effects.apis.detectLanguage(detectData).then((response) => {
        const originalLanguage = find(
          propEq('id', response.id),
          state.languages,
        )
        const newLanguages = isNil(originalLanguage)
          ? append(mergeRight(response, { count: 0 }), state.languages)
          : state.languages

        dispatch({
          type: actionType.DETECT_LANGUAGE_SUCCESS,
          languages: newLanguages,
          originalLanguageId: response.id,
        })
      })
    }
  }

  const onCloseLanguageSelected = () => {
    dispatch({ type: actionType.CLOSE_LANGUAGE_SELECTOR })
  }

  const clearLanguageSelected = () => {
    dispatch({ type: actionType.CLEAR_LANGUAGE_SELECTED })
  }

  return {
    state,
    apiFetchersLanguagesCallback,
    apiFetchersLanguagesCallbackCompose,
    updateOriginalText,
    updateLanguageSelected,
    translateText,
    onOpenLanguageSelected,
    onCloseLanguageSelected,
    clearLanguageSelected,
    onOpenSelected,
  }
}
